import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { GameSettings, CountingSystem, LevelingConfig } from '../types';
import * as sounds from '../utils/sounds';
// Note: Firebase persistence is handled by gameStore subscriber, not this store

const DEFAULT_LEVELING: LevelingConfig = {
  baseXP: 100,
  increment: 25,
  maxLevel: 50,
  stages: [
    { level: 1, title: 'Newcomer' },
    { level: 5, title: 'Beginner' },
    { level: 10, title: 'Novice' },
    { level: 15, title: 'Apprentice' },
    { level: 20, title: 'Card Counter' },
    { level: 25, title: 'Skilled Player' },
    { level: 30, title: 'Professional' },
    { level: 35, title: 'Expert Counter' },
    { level: 40, title: 'Master Counter' },
    { level: 45, title: 'Legendary Counter' },
  ],
};

export const DEFAULT_SETTINGS: GameSettings = {
  numDecks: 6,
  penetration: 75,
  dealerHitsSoft17: true,
  doubleAfterSplit: true,
  surrenderAllowed: false,
  earlySurrenderAllowed: false,
  resplitAces: false,
  blackjackPays: 1.5,
  countingSystem: 'hi-lo',
  minBet: 25,
  maxBet: 500,
  shuffleAfterHand: false,
  startingBankroll: 10000,
  insuranceAllowed: true,
  useDeviations: false,
  soundEnabled: true,
  highContrast: false,
  hideHandTotals: false,
  casinoName: 'JK Casino',
  feedbackMode: 'bad-only',
  leveling: DEFAULT_LEVELING,
  customAchievements: [],
  achievementEmojiOptions: ['🎯', '🏆', '⭐', '🔥', '💎', '🚀', '🧠', '💰', '⚡', '📈', '🎰'],
  defaultSpeedDrillDuration: 60,
  defaultSpeedDrillSpeed: 1000,
  aiThinkTimeMinMs: 500,
  aiThinkTimeMaxMs: 2000,
  aiMistakeRate: 0,
  devMode: false
};

interface SettingsStore {
  settings: GameSettings;
  
  // Actions
  updateSettings: (settings: Partial<GameSettings>) => void;
  resetSettings: () => void;
  setCountingSystem: (system: CountingSystem) => void;
  setNumDecks: (numDecks: number) => void;
  toggleSound: () => void;
  toggleHighContrast: () => void;
  toggleDeviations: () => void;
}

export const useSettingsStore = create<SettingsStore>()(
  subscribeWithSelector((set, get) => ({
    settings: { ...DEFAULT_SETTINGS },

    updateSettings: (newSettings) => {
      const currentSettings = get().settings;
      const settings = { ...currentSettings, ...newSettings };

      // Sync sound setting
      if (newSettings.soundEnabled !== undefined) {
        sounds.setSoundEnabled(newSettings.soundEnabled);
      }

      set({ settings });
      // Note: Firebase persistence is handled by gameStore subscriber
    },

    resetSettings: () => {
      sounds.setSoundEnabled(DEFAULT_SETTINGS.soundEnabled);
      set({ settings: DEFAULT_SETTINGS });
    },

    setCountingSystem: (system) => {
      get().updateSettings({ countingSystem: system });
    },

    setNumDecks: (numDecks) => {
      get().updateSettings({ numDecks });
    },

    toggleSound: () => {
      const currentSettings = get().settings;
      get().updateSettings({ soundEnabled: !currentSettings.soundEnabled });
    },

    toggleHighContrast: () => {
      const currentSettings = get().settings;
      get().updateSettings({ highContrast: !currentSettings.highContrast });
    },

    toggleDeviations: () => {
      const currentSettings = get().settings;
      get().updateSettings({ useDeviations: !currentSettings.useDeviations });
    }
  }))
);
